/**
 * Trinity Basics - Shape Previews
 *
 * Entry point for the OEG Museum. Renders 12 interactive canvas previews
 * demonstrating fundamental shapes and omniequivalent geometry lattices.
 */

import { BackgroundSystem } from '@trinity/background-system';
import { TRINITY_PALETTE } from './core/types';

// --- Background Animation ---
const bgCanvas = document.getElementById('bg-canvas') as HTMLCanvasElement;
if (bgCanvas) {
  new BackgroundSystem(bgCanvas, {
    sideLength: 60,
    animation: 'breathe',
    animationConfig: { speed: 0.25, intensity: 0.25 },
    baseOpacity: 0.35,
    backgroundColor: '#0a0f1a',
  });
}

// --- Math helpers ---
const SQRT3 = Math.sqrt(3);
function triHeight(side: number): number {
  return (side * SQRT3) / 2;
}

const BG_COLOR = '#0f172a';

// --- Canvas setup (DPR-aware) ---
function setupCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const rect = canvas.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  return ctx;
}

// --- Drawing primitives ---

function drawEquilateralUp(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  side: number,
): [number, number][] {
  const h = triHeight(side);
  const pts: [number, number][] = [
    [cx, cy - (2 / 3) * h],
    [cx - side / 2, cy + (1 / 3) * h],
    [cx + side / 2, cy + (1 / 3) * h],
  ];
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  ctx.lineTo(pts[1][0], pts[1][1]);
  ctx.lineTo(pts[2][0], pts[2][1]);
  ctx.closePath();
  return pts;
}

function drawEquilateralDown(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  side: number,
): [number, number][] {
  const h = triHeight(side);
  const pts: [number, number][] = [
    [cx, cy + (2 / 3) * h],
    [cx - side / 2, cy - (1 / 3) * h],
    [cx + side / 2, cy - (1 / 3) * h],
  ];
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  ctx.lineTo(pts[1][0], pts[1][1]);
  ctx.lineTo(pts[2][0], pts[2][1]);
  ctx.closePath();
  return pts;
}

// --- Color helper ---

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// --- Shape renderers ---

type RenderFn = (
  ctx: CanvasRenderingContext2D,
  time: number,
  w: number,
  h: number,
  hoverT: number,
) => void;

const TAU = Math.PI * 2;

const renderTriangle: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h / 2;
  const side = 130;
  const breathe = Math.sin(time * 1.2) * 0.03;
  const scale = 1.0 + breathe + hoverT * 0.1;
  const rotation = Math.sin(time * 0.8) * (2 + hoverT * 6) * (Math.PI / 180);

  // Ambient radial glow
  const glowA = 0.06 + Math.sin(time * 1.5) * 0.03 + hoverT * 0.08;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, side);
  grad.addColorStop(0, hexToRgba(TRINITY_PALETTE.lightBlue, glowA));
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);
  ctx.scale(scale, scale);

  ctx.shadowColor = TRINITY_PALETTE.lightBlue;
  ctx.shadowBlur = 12 + hoverT * 25;

  // Gradient fill
  drawEquilateralUp(ctx, 0, 0, side);
  const tg = ctx.createLinearGradient(0, -triHeight(side) * 0.67, 0, triHeight(side) * 0.33);
  tg.addColorStop(0, TRINITY_PALETTE.lightBlue);
  tg.addColorStop(1, TRINITY_PALETTE.mediumBlue);
  ctx.fillStyle = tg;
  ctx.fill();

  // Edge highlight
  ctx.shadowBlur = 0;
  drawEquilateralUp(ctx, 0, 0, side);
  ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.white, 0.15 + hoverT * 0.1);
  ctx.lineWidth = 1;
  ctx.stroke();

  // Vertex dots on hover
  if (hoverT > 0.1) {
    const pts = drawEquilateralUp(ctx, 0, 0, side);
    for (const [px, py] of pts) {
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, TAU);
      ctx.fillStyle = hexToRgba(TRINITY_PALETTE.white, hoverT * 0.8);
      ctx.fill();
    }
  }

  // --- EM field lines along the 3 medians (hover only) ---
  if (hoverT > 0.02) {
    const hf = triHeight(side);
    // Triangle vertices (in local coords centered at 0,0)
    const A: [number, number] = [0, -(2 / 3) * hf];           // top
    const B: [number, number] = [-side / 2, (1 / 3) * hf];    // bottom-left
    const C: [number, number] = [side / 2, (1 / 3) * hf];     // bottom-right

    // Each median: corner → midpoint of opposite edge
    const medians: { from: [number, number]; to: [number, number]; color: string }[] = [
      { from: A, to: [(B[0] + C[0]) / 2, (B[1] + C[1]) / 2], color: TRINITY_PALETTE.lightBlue },
      { from: B, to: [(A[0] + C[0]) / 2, (A[1] + C[1]) / 2], color: TRINITY_PALETTE.mediumBlue },
      { from: C, to: [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2], color: TRINITY_PALETTE.darkBlue },
    ];

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    const FIELD_LINES = 7;          // parallel lines per median
    const SEGMENTS = 48;            // smoothness per line
    const SPREAD = 14;              // max lateral spread of outermost line
    const FREQ_MAJOR = 5;           // primary wave frequency
    const FREQ_MINOR = 13;          // secondary (guilloche) frequency

    for (let mi = 0; mi < medians.length; mi++) {
      const { from, to, color } = medians[mi];
      const dx = to[0] - from[0];
      const dy = to[1] - from[1];
      const len = Math.sqrt(dx * dx + dy * dy);
      // Direction and perpendicular unit vectors
      const ux = dx / len;
      const uy = dy / len;
      const nx = -uy;   // perpendicular
      const ny = ux;

      for (let fi = 0; fi < FIELD_LINES; fi++) {
        const blend = (fi / (FIELD_LINES - 1)) * 2 - 1;   // -1 to +1
        const lateralOffset = blend * SPREAD * hoverT;
        // Edge lines fade, center brightest
        const edgeFade = 1 - Math.abs(blend) * 0.6;
        const alpha = hoverT * 0.35 * edgeFade;

        ctx.beginPath();
        for (let s = 0; s <= SEGMENTS; s++) {
          const t = s / SEGMENTS;
          // Envelope: zero at endpoints, max in the middle
          const envelope = Math.sin(t * Math.PI);
          // Dual-frequency wave inspired by trinity-symbol guilloche
          const phase = time * (2.5 + mi * 0.4) + mi * TAU / 3;
          const wave1 = Math.sin(FREQ_MAJOR * t * TAU + phase + fi * 0.3);
          const wave2 = Math.sin(FREQ_MINOR * t * TAU - phase * 0.7 + fi * 0.5) * 0.4;
          const waveDisp = (wave1 + wave2) * envelope * hoverT * 8;

          const px = from[0] + dx * t + nx * (lateralOffset + waveDisp);
          const py = from[1] + dy * t + ny * (lateralOffset + waveDisp);

          s === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.strokeStyle = hexToRgba(color, alpha);
        ctx.lineWidth = 0.6 + (1 - Math.abs(blend)) * 0.6;
        ctx.stroke();
      }

      // Bright core line (the median itself, with subtle pulse)
      const corePulse = 0.5 + Math.sin(time * 3 + mi * TAU / 3) * 0.3;
      ctx.beginPath();
      for (let s = 0; s <= SEGMENTS; s++) {
        const t = s / SEGMENTS;
        const envelope = Math.sin(t * Math.PI);
        const phase = time * 3 + mi * TAU / 3;
        const coreWave = Math.sin(FREQ_MINOR * t * TAU + phase) * envelope * hoverT * 3;
        const px = from[0] + dx * t + nx * coreWave;
        const py = from[1] + dy * t + ny * coreWave;
        s === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.strokeStyle = hexToRgba(color, hoverT * 0.6 * corePulse);
      ctx.lineWidth = 1.8;
      ctx.stroke();

      // Endpoint energy nodes (at corner and midpoint)
      for (const pt of [from, to]) {
        const nodePulse = 0.4 + Math.sin(time * 4 + mi * 2) * 0.3;
        const nodeR = 2 + hoverT * 3 * nodePulse;
        const nodeGrad = ctx.createRadialGradient(pt[0], pt[1], 0, pt[0], pt[1], nodeR * 2);
        nodeGrad.addColorStop(0, hexToRgba(color, hoverT * 0.7 * nodePulse));
        nodeGrad.addColorStop(1, hexToRgba(color, 0));
        ctx.fillStyle = nodeGrad;
        ctx.beginPath();
        ctx.arc(pt[0], pt[1], nodeR * 2, 0, TAU);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  ctx.restore();
};

const renderHexagon: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h / 2;
  const side = 55;
  const colors = [
    TRINITY_PALETTE.lightBlue,
    TRINITY_PALETTE.mediumBlue,
    TRINITY_PALETTE.darkBlue,
    TRINITY_PALETTE.deepBlue,
    TRINITY_PALETTE.mediumBlue,
    TRINITY_PALETTE.lightBlue,
  ];

  // Ambient glow
  const glowA = 0.05 + Math.sin(time * 1.2) * 0.02 + hoverT * 0.06;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, side * 1.5);
  grad.addColorStop(0, hexToRgba(TRINITY_PALETTE.mediumBlue, glowA));
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const breatheRot = Math.sin(time * 0.5) * 0.5 * (Math.PI / 180);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(breatheRot);

  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    const nextAngle = (Math.PI / 3) * (i + 1) - Math.PI / 2;

    const centroidX = (Math.cos(angle) + Math.cos(nextAngle)) * side / 3;
    const centroidY = (Math.sin(angle) + Math.sin(nextAngle)) * side / 3;
    const sep = 4 * hoverT;
    const len = Math.sqrt(centroidX * centroidX + centroidY * centroidY) || 1;

    ctx.save();
    ctx.translate((centroidX / len) * sep, (centroidY / len) * sep);

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(angle) * side, Math.sin(angle) * side);
    ctx.lineTo(Math.cos(nextAngle) * side, Math.sin(nextAngle) * side);
    ctx.closePath();

    // Per-wedge pulse
    const pulse = (Math.sin(time * 1.5 + i * 0.8) + 1) / 2;
    const baseColor = colors[i];
    ctx.fillStyle = baseColor;
    ctx.fill();

    // Glow overlay on hover
    if (hoverT > 0.05) {
      ctx.fillStyle = hexToRgba(TRINITY_PALETTE.lightBlue, pulse * hoverT * 0.2);
      ctx.fill();
    }

    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.white, 0.1 + hoverT * 0.15);
    ctx.lineWidth = 0.5;
    ctx.stroke();

    ctx.restore();
  }

  ctx.restore();
};

const renderDiamond: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h / 2;
  const side = 85;
  const hh = triHeight(side);
  const stretchY = hoverT * 6;
  const breathe = Math.sin(time * 1.0) * 2;
  const rotation = Math.sin(time * 0.7) * (1 + hoverT * 5) * (Math.PI / 180);

  // Ambient glow
  const glowA = 0.04 + Math.sin(time * 1.3) * 0.02 + hoverT * 0.06;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, side);
  grad.addColorStop(0, hexToRgba(TRINITY_PALETTE.lightBlue, glowA));
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);
  ctx.scale(1 - hoverT * 0.04, 1 + hoverT * 0.06);

  ctx.shadowColor = TRINITY_PALETTE.mediumBlue;
  ctx.shadowBlur = 8 + hoverT * 15;

  // Up triangle — gradient fill
  drawEquilateralUp(ctx, 0, -hh / 6 - stretchY / 2 - breathe * 0.3, side);
  const topGrad = ctx.createLinearGradient(0, -hh, 0, 0);
  topGrad.addColorStop(0, TRINITY_PALETTE.lightBlue);
  topGrad.addColorStop(1, TRINITY_PALETTE.mediumBlue);
  ctx.fillStyle = topGrad;
  ctx.fill();
  ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.white, 0.12);
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // Down triangle — gradient fill
  drawEquilateralDown(ctx, 0, hh / 6 + stretchY / 2 + breathe * 0.3, side);
  const botGrad = ctx.createLinearGradient(0, 0, 0, hh);
  botGrad.addColorStop(0, TRINITY_PALETTE.darkBlue);
  botGrad.addColorStop(1, TRINITY_PALETTE.deepBlue);
  ctx.fillStyle = botGrad;
  ctx.fill();
  ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, 0.12);
  ctx.lineWidth = 0.8;
  ctx.stroke();

  ctx.shadowBlur = 0;

  // Mirror axis line on hover
  if (hoverT > 0.2) {
    ctx.beginPath();
    ctx.moveTo(-side / 2 - 8, 0);
    ctx.lineTo(side / 2 + 8, 0);
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, hoverT * 0.3);
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();
};

const renderTriforce: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h / 2;
  const side = 58;
  const hh = triHeight(side);

  const positions: { x: number; y: number; color: string }[] = [
    { x: 0, y: -hh * 2 / 3, color: TRINITY_PALETTE.lightBlue },
    { x: -side / 2, y: hh / 3, color: TRINITY_PALETTE.mediumBlue },
    { x: side / 2, y: hh / 3, color: TRINITY_PALETTE.darkBlue },
  ];

  // Ambient glow
  const glowA = 0.05 + Math.sin(time * 1.0) * 0.02 + hoverT * 0.06;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, side * 1.2);
  grad.addColorStop(0, hexToRgba(TRINITY_PALETTE.lightBlue, glowA));
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const breatheRot = Math.sin(time * 0.6) * 0.8 * (Math.PI / 180);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(breatheRot);

  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i];
    const sep = 6 * hoverT;
    const len = Math.sqrt(pos.x * pos.x + pos.y * pos.y) || 1;
    const ox = (pos.x / len) * sep;
    const oy = (pos.y / len) * sep;

    // Per-triangle breathe offset
    const tb = Math.sin(time * 1.2 + i * TAU / 3) * 1.5;

    ctx.save();
    ctx.translate(ox + tb * (pos.x / len) * 0.3, oy + tb * (pos.y / len) * 0.3);

    // Per-triangle glow
    ctx.shadowColor = pos.color;
    ctx.shadowBlur = 6 + hoverT * 12;

    drawEquilateralUp(ctx, pos.x, pos.y, side);
    ctx.fillStyle = pos.color;
    ctx.fill();
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.white, 0.12 + hoverT * 0.1);
    ctx.lineWidth = 0.5;
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.restore();
  }

  // Center void triangle outline on hover
  if (hoverT > 0.3) {
    const voidA = (hoverT - 0.3) * 1.4;
    drawEquilateralDown(ctx, 0, hh / 3 - hh * 2 / 3 + hh / 3, side);
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, voidA * 0.25);
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();
};

const renderGrid: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const side = 30;
  const hh = triHeight(side);
  const halfSide = side / 2;
  const colCount = Math.ceil(w / hh) + 2;

  ctx.lineWidth = 1.2;

  // Vertical lines — per-column wave alpha for metallic shimmer
  for (let col = 0; col <= colCount; col++) {
    const x = col * hh;
    const wave = (Math.sin(time * 0.8 + col * 0.3) + 1) / 2;
    const alpha = 0.2 + wave * 0.25 + hoverT * 0.35;
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, alpha);
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }

  // Diagonals — wave alpha per segment for ripple effect
  for (let y = 0; y <= h + side; y += side) {
    for (let col = 0; col < colCount; col++) {
      const wave = (Math.sin(time * 0.6 + col * 0.4 + y * 0.01) + 1) / 2;
      const alpha = 0.15 + wave * 0.2 + hoverT * 0.3;
      ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, alpha);
      ctx.lineWidth = 1.2;

      // Down-leaning
      const y0d = col % 2 === 0 ? y : y + halfSide;
      ctx.beginPath();
      ctx.moveTo(col * hh, y0d);
      ctx.lineTo((col + 1) * hh, y0d - halfSide);
      ctx.stroke();

      // Up-leaning
      const y0u = col % 2 === 0 ? y : y - halfSide;
      ctx.beginPath();
      ctx.moveTo(col * hh, y0u);
      ctx.lineTo((col + 1) * hh, y0u + halfSide);
      ctx.stroke();
    }
  }
};

// Correct hex grid: triangle grid with inner edges hidden.
// 3-color vertices; color=0 = hex center. Only draw edges where NEITHER endpoint is color=0.
const renderHexGrid: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const side = 26;
  const hh = triHeight(side);
  const hs = side / 2;

  const vxOf = (col: number) => col * hh;
  const vyOf = (col: number, row: number) => row * side + (col & 1) * hs;
  const vertColor = (col: number, row: number) =>
    (((row + (col & 1) * 2) % 3) + 3) % 3;

  const colCount = Math.ceil(w / hh) + 3;
  const rowCount = Math.ceil(h / side) + 3;

  ctx.lineWidth = 1.3;

  // Draw each hex boundary edge with metallic wave alpha
  const drawEdge = (x1: number, y1: number, x2: number, y2: number) => {
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dist = Math.sqrt((mx - w / 2) ** 2 + (my - h / 2) ** 2);
    const wave = (Math.sin(time * 0.7 - dist * 0.012) + 1) / 2;
    const alpha = 0.18 + wave * 0.35 + hoverT * 0.3;
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, Math.min(alpha, 0.85));
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  };

  for (let col = -2; col <= colCount; col++) {
    for (let row = -2; row <= rowCount; row++) {
      const c0 = vertColor(col, row);

      // Vertical: (col, row) → (col, row+1)
      if (c0 !== 0 && vertColor(col, row + 1) !== 0) {
        drawEdge(vxOf(col), vyOf(col, row), vxOf(col), vyOf(col, row + 1));
      }

      if ((col & 1) === 0) {
        // Even col diagonals
        if (c0 !== 0 && vertColor(col + 1, row - 1) !== 0) {
          drawEdge(vxOf(col), vyOf(col, row), vxOf(col + 1), vyOf(col + 1, row - 1));
        }
        if (c0 !== 0 && vertColor(col + 1, row) !== 0) {
          drawEdge(vxOf(col), vyOf(col, row), vxOf(col + 1), vyOf(col + 1, row));
        }
      } else {
        // Odd col diagonals
        if (c0 !== 0 && vertColor(col + 1, row) !== 0) {
          drawEdge(vxOf(col), vyOf(col, row), vxOf(col + 1), vyOf(col + 1, row));
        }
        if (c0 !== 0 && vertColor(col + 1, row + 1) !== 0) {
          drawEdge(vxOf(col), vyOf(col, row), vxOf(col + 1), vyOf(col + 1, row + 1));
        }
      }
    }
  }
};

// BCC — Body-Centered Cubic: square grid with center-to-corner diagonals
const renderBCC: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  // Fade background toward black on hover so the grid appears without shadow
  const bgAlpha = 1 - hoverT * 0.6;
  ctx.fillStyle = hexToRgba(BG_COLOR, bgAlpha);
  ctx.fillRect(0, 0, w, h);

  const spacing = 40;
  const cols = Math.ceil(w / spacing) + 1;
  const rows = Math.ceil(h / spacing) + 1;

  ctx.lineWidth = 1.0;

  // Collect all edges once to avoid double-stroking shared edges
  const edges: [number, number, number, number][] = [];

  for (let row = 0; row <= rows; row++) {
    for (let col = 0; col <= cols; col++) {
      const x = col * spacing;
      const y = row * spacing;

      // Right edge (avoid duplicate: only draw from this vertex)
      if (col < cols) edges.push([x, y, x + spacing, y]);
      // Down edge
      if (row < rows) edges.push([x, y, x, y + spacing]);

      // Diagonals to cell center (only for cells, not boundary vertices)
      if (col < cols && row < rows) {
        const cx = x + spacing / 2;
        const cy = y + spacing / 2;
        edges.push([x, y, cx, cy]);
        edges.push([x + spacing, y, cx, cy]);
        edges.push([x, y + spacing, cx, cy]);
        edges.push([x + spacing, y + spacing, cx, cy]);
      }
    }
  }

  // Draw all edges with uniform weight — brighten on hover
  for (const [x1, y1, x2, y2] of edges) {
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const wave = (Math.sin(time * 0.6 + mx * 0.03 + my * 0.025) + 1) / 2;
    const alpha = 0.15 + wave * 0.25 + hoverT * 0.45;
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, alpha);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
};

// Simple Cubic — pure horizontal + vertical lines
const renderCubic: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const spacing = 36;
  ctx.lineWidth = 1.2;

  // Vertical lines
  const cols = Math.ceil(w / spacing) + 1;
  for (let i = 0; i <= cols; i++) {
    const x = i * spacing;
    const wave = (Math.sin(time * 0.7 + i * 0.35) + 1) / 2;
    const alpha = 0.18 + wave * 0.3 + hoverT * 0.3;
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, alpha);
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }

  // Horizontal lines
  const rows = Math.ceil(h / spacing) + 1;
  for (let i = 0; i <= rows; i++) {
    const y = i * spacing;
    const wave = (Math.sin(time * 0.7 + i * 0.35 + 1.5) + 1) / 2;
    const alpha = 0.18 + wave * 0.3 + hoverT * 0.3;
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, alpha);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
};

// HCP — Hexagonal Close-Packed: top-down view of two hex layers (A+B)
const renderHCP: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const R = 24;
  const dx = R * 3;
  const dy = R * SQRT3;
  const cols = Math.ceil(w / dx) + 2;
  const rows = Math.ceil(h / dy) + 2;

  // Layer A — hex centers on regular hex grid (blue)
  const layerA: [number, number][] = [];
  for (let row = -1; row < rows; row++) {
    const offsetX = (row % 2 !== 0) ? dx / 2 : 0;
    for (let col = -1; col < cols; col++) {
      layerA.push([col * dx + offsetX, row * dy]);
    }
  }

  // Layer B — offset by (R, R*sqrt3/3) sitting in the A-layer hollows (amber)
  const layerB: [number, number][] = [];
  for (let row = -1; row < rows; row++) {
    const offsetX = (row % 2 !== 0) ? dx / 2 : 0;
    for (let col = -1; col < cols; col++) {
      layerB.push([col * dx + offsetX + R, row * dy + R * SQRT3 / 3]);
    }
  }

  // Draw A-layer hexagonal outlines (faint)
  ctx.lineWidth = 0.7;
  for (const [cx, cy] of layerA) {
    const wave = (Math.sin(time * 0.4 + cx * 0.01 + cy * 0.01) + 1) / 2;
    const alpha = 0.08 + wave * 0.12 + hoverT * 0.15;
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, alpha);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      const vx = cx + R * Math.cos(angle);
      const vy = cy + R * Math.sin(angle);
      i === 0 ? ctx.moveTo(vx, vy) : ctx.lineTo(vx, vy);
    }
    ctx.closePath();
    ctx.stroke();
  }

  // Draw A-layer atoms (larger, bright blue)
  for (const [px, py] of layerA) {
    const wave = (Math.sin(time * 0.7 + px * 0.02 + py * 0.02) + 1) / 2;
    const alpha = 0.4 + wave * 0.35 + hoverT * 0.25;
    const r = 5 + hoverT * 2;
    ctx.beginPath();
    ctx.arc(px, py, r, 0, TAU);
    ctx.fillStyle = hexToRgba(TRINITY_PALETTE.lightBlue, alpha);
    ctx.fill();
  }

  // Draw B-layer atoms (smaller, amber/gold — offset into hollows)
  for (const [px, py] of layerB) {
    const wave = (Math.sin(time * 0.7 + px * 0.02 + py * 0.02 + 2) + 1) / 2;
    const alpha = 0.3 + wave * 0.3 + hoverT * 0.25;
    const r = 3.5 + hoverT * 1.5;
    ctx.beginPath();
    ctx.arc(px, py, r, 0, TAU);
    ctx.fillStyle = hexToRgba('#f59e0b', alpha);
    ctx.fill();
  }
};

// Rhombille — 3 rhombi per hex, connecting hex center to vertex pairs
const renderRhombille: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const hexR = 36;
  const hexW = hexR * 1.5;
  const hexH = hexR * SQRT3;

  const cols = Math.ceil(w / hexW) + 3;
  const rows = Math.ceil(h / hexH) + 3;

  const edgeSet = new Set<string>();
  const edgeKey = (x1: number, y1: number, x2: number, y2: number) => {
    const ax = Math.round(x1 * 10), ay = Math.round(y1 * 10);
    const bx = Math.round(x2 * 10), by = Math.round(y2 * 10);
    return ax < bx || (ax === bx && ay < by)
      ? `${ax},${ay}-${bx},${by}`
      : `${bx},${by}-${ax},${ay}`;
  };

  ctx.lineWidth = 1.0;

  for (let row = -1; row < rows; row++) {
    for (let col = -1; col < cols; col++) {
      const offsetY = (col % 2 !== 0) ? hexH / 2 : 0;
      const cx = col * hexW;
      const cy = row * hexH + offsetY;

      // 6 vertices of hex
      const verts: [number, number][] = [];
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        verts.push([cx + hexR * Math.cos(angle), cy + hexR * Math.sin(angle)]);
      }

      // Draw rhombi: center to each pair of adjacent vertices
      for (let i = 0; i < 6; i++) {
        const v1 = verts[i];
        const v2 = verts[(i + 1) % 6];
        const edges: [number, number, number, number][] = [
          [cx, cy, v1[0], v1[1]],
          [cx, cy, v2[0], v2[1]],
          [v1[0], v1[1], v2[0], v2[1]],
        ];
        for (const [x1, y1, x2, y2] of edges) {
          const key = edgeKey(x1, y1, x2, y2);
          if (!edgeSet.has(key)) {
            edgeSet.add(key);
            const mx = (x1 + x2) / 2;
            const my = (y1 + y2) / 2;
            const wave = (Math.sin(time * 0.6 + mx * 0.015 + my * 0.015) + 1) / 2;
            const alpha = 0.15 + wave * 0.3 + hoverT * 0.3;
            ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, Math.min(alpha, 0.85));
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          }
        }
      }
    }
  }
};

// Kagome — triangle grid with 1/3 triangles skipped (3.6.3.6 trihexagonal)
const renderKagome: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const side = 28;
  const hh = triHeight(side);
  const halfSide = side / 2;

  const colCount = Math.ceil(w / hh) + 3;
  const rowCount = Math.ceil(h / side) + 3;

  const edgeSet = new Set<string>();
  const edgeKey = (x1: number, y1: number, x2: number, y2: number) => {
    const ax = Math.round(x1 * 10), ay = Math.round(y1 * 10);
    const bx = Math.round(x2 * 10), by = Math.round(y2 * 10);
    return ax < bx || (ax === bx && ay < by)
      ? `${ax},${ay}-${bx},${by}`
      : `${bx},${by}-${ax},${ay}`;
  };

  ctx.lineWidth = 1.1;

  for (let col = -2; col <= colCount; col++) {
    for (let row = -2; row <= rowCount; row++) {
      const x = col * hh;
      const yBase = row * side + (col & 1) * halfSide;

      // Skip every 3rd triangle position to create kagome pattern
      if ((row + col) % 3 === 0) continue;

      // Draw edges to neighbors (vertical and diagonals)
      const neighbors: [number, number][] = [
        [col, row + 1],     // vertical neighbor
        [col + 1, row + ((col & 1) === 0 ? -1 : 0)], // diagonal
        [col + 1, row + ((col & 1) === 0 ? 0 : 1)],  // diagonal
      ];

      for (const [nc, nr] of neighbors) {
        if ((nr + nc) % 3 === 0) continue;

        const nx = nc * hh;
        const ny = nr * side + (nc & 1) * halfSide;

        const key = edgeKey(x, yBase, nx, ny);
        if (!edgeSet.has(key)) {
          edgeSet.add(key);
          const mx = (x + nx) / 2;
          const my = (yBase + ny) / 2;
          const wave = (Math.sin(time * 0.7 + mx * 0.02 - my * 0.01) + 1) / 2;
          const alpha = 0.15 + wave * 0.3 + hoverT * 0.3;
          ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.lightBlue, Math.min(alpha, 0.85));
          ctx.beginPath();
          ctx.moveTo(x, yBase);
          ctx.lineTo(nx, ny);
          ctx.stroke();
        }
      }
    }
  }
};

// Diamond Cubic — 2D isometric projection of 3D diamond crystal
const renderDiamondCubic: RenderFn = (ctx, time, w, h, hoverT) => {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, w, h);

  const scale = 32;
  const rotSpeed = 0.15;
  const angle = time * rotSpeed;
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);

  // Isometric projection
  const project = (x: number, y: number, z: number): [number, number, number] => {
    // Rotate around Y axis
    const rx = x * cosA - z * sinA;
    const rz = x * sinA + z * cosA;
    // Isometric projection
    const px = (rx - y) * 0.866;
    const py = (rx + y) * 0.5 - rz * 0.75;
    return [w / 2 + px * scale, h / 2 + py * scale, rz];
  };

  // FCC lattice 1 (corners + face centers)
  const fcc1: [number, number, number][] = [];
  // FCC lattice 2 (offset by 1/4, 1/4, 1/4)
  const fcc2: [number, number, number][] = [];

  const range = 2;
  for (let i = -range; i <= range; i++) {
    for (let j = -range; j <= range; j++) {
      for (let k = -range; k <= range; k++) {
        fcc1.push([i, j, k]);
        fcc2.push([i + 0.25, j + 0.25, k + 0.25]);
      }
    }
  }

  const allPoints = [...fcc1, ...fcc2];
  const bonds: [number, number][] = [];

  // Connect each fcc2 point to its 4 nearest fcc1 neighbors (tetrahedral bonds)
  for (let i = 0; i < fcc2.length; i++) {
    const p2 = fcc2[i];
    const distances: { idx: number; dist: number }[] = [];
    for (let j = 0; j < fcc1.length; j++) {
      const dx = p2[0] - fcc1[j][0];
      const dy = p2[1] - fcc1[j][1];
      const dz = p2[2] - fcc1[j][2];
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      distances.push({ idx: j, dist });
    }
    distances.sort((a, b) => a.dist - b.dist);
    for (let n = 0; n < Math.min(4, distances.length); n++) {
      if (distances[n].dist < 0.55) {
        bonds.push([fcc1.length + i, distances[n].idx]);
      }
    }
  }

  // Project all points
  const projected = allPoints.map(p => project(p[0], p[1], p[2]));

  // Draw bonds
  ctx.lineWidth = 1.0;
  for (const [a, b] of bonds) {
    const [ax, ay, az] = projected[a];
    const [bx, by, bz] = projected[b];
    const avgDepth = (az + bz) / 2;
    const depthAlpha = 0.3 + (1 - (avgDepth + range) / (range * 2)) * 0.4;
    const wave = (Math.sin(time * 0.5 + ax * 0.01 + ay * 0.01) + 1) / 2;
    const alpha = (0.1 + wave * 0.2 + hoverT * 0.25) * depthAlpha;
    ctx.strokeStyle = hexToRgba(TRINITY_PALETTE.mediumBlue, Math.min(alpha, 0.8));
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.stroke();
  }

  // Draw points
  for (let i = 0; i < projected.length; i++) {
    const [px, py, pz] = projected[i];
    if (px < -20 || px > w + 20 || py < -20 || py > h + 20) continue;
    const depthAlpha = 0.4 + (1 - (pz + range) / (range * 2)) * 0.5;
    const wave = (Math.sin(time * 0.8 + px * 0.02 + py * 0.02) + 1) / 2;
    const alpha = (0.2 + wave * 0.4 + hoverT * 0.3) * depthAlpha;
    const r = (i < fcc1.length ? 3 : 2.5) + hoverT * 1;
    const color = i < fcc1.length ? TRINITY_PALETTE.lightBlue : TRINITY_PALETTE.mediumBlue;
    ctx.beginPath();
    ctx.arc(px, py, r, 0, TAU);
    ctx.fillStyle = hexToRgba(color, alpha);
    ctx.fill();
  }
};

// --- Shape registry ---

const shapes: { id: string; render: RenderFn }[] = [
  // Fundamentals (4)
  { id: 'preview-triangle', render: renderTriangle },
  { id: 'preview-hexagon', render: renderHexagon },
  { id: 'preview-diamond', render: renderDiamond },
  { id: 'preview-triforce', render: renderTriforce },
  // True OEG (3) — 60° / 90° / 120°
  { id: 'preview-grid', render: renderGrid },
  { id: 'preview-cubic', render: renderCubic },
  { id: 'preview-hex-grid', render: renderHexGrid },
];

// --- Bootstrap all canvases ---

const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

for (const shape of shapes) {
  const canvas = document.getElementById(shape.id) as HTMLCanvasElement | null;
  if (!canvas) continue;

  const ctx = setupCanvas(canvas);
  const rect = canvas.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;

  let isHovered = false;
  let hoverT = 0;

  // Track hover on parent card, not just canvas
  const card = canvas.closest('.shape-card') as HTMLElement | null;
  const hoverTarget = card || canvas;
  hoverTarget.addEventListener('mouseenter', () => { isHovered = true; });
  hoverTarget.addEventListener('mouseleave', () => { isHovered = false; });

  // Touch: tap to toggle active state (stays until tap again or another card tapped)
  hoverTarget.addEventListener('click', (e) => {
    if (!isTouchDevice) return;
    e.stopPropagation();
    if (isHovered) {
      isHovered = false;
      card?.classList.remove('is-active');
    } else {
      // Deactivate any other active card
      document.querySelectorAll('.shape-card.is-active').forEach((el) => {
        el.classList.remove('is-active');
        el.dispatchEvent(new CustomEvent('deactivate'));
      });
      isHovered = true;
      card?.classList.add('is-active');
    }
  });
  hoverTarget.addEventListener('deactivate', () => { isHovered = false; });

  const startTime = performance.now();

  const animate = () => {
    const time = (performance.now() - startTime) / 1000;
    const target = isHovered ? 1 : 0;
    hoverT += (target - hoverT) * 0.06;

    shape.render(ctx, time, w, h, hoverT);
    requestAnimationFrame(animate);
  };

  requestAnimationFrame(animate);
}

// --- Keyboard ---

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    window.location.href = '/';
  }
});

console.log('\uD83D\uDD3A Trinity Basics');
